import React, { useState } from 'react';
import styled from 'styled-components';
import { devices } from '../data/constants';
import { IoChevronDown, IoHelpCircleOutline } from 'react-icons/io5';

const PageContainer = styled.div`
    min-height: calc(100vh - 160px);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 40px 20px;
    background: transparent;

    @media only screen and ${devices.md} {
        padding: 20px 16px;
    }
`;

const FAQCard = styled.div`
    background: ${({ theme }) => theme.colors.cardBG};
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    padding: 40px;
    width: 100%;
    max-width: 720px;

    @media only screen and ${devices.md} {
        padding: 24px 16px;
        border-radius: 12px;
    }
`;

const HeaderSection = styled.div`
    text-align: center;
    margin-bottom: 32px;
`;

const IconWrapper = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.activeColor}15;
    color: ${({ theme }) => theme.colors.activeColor};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    font-size: 28px;
`;

const Title = styled.h1`
    font-size: 24px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.cardText};
    margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
    font-size: 14px;
    color: ${({ theme }) => theme.colors.subTextColor};
    margin: 0;
    line-height: 1.5;
`;

const Section = styled.section`
    margin-bottom: 28px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.h3`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.subTextColor};
    margin: 0 0 12px 0;
`;

const FAQItem = styled.div`
    background: ${({ theme }) => theme.colors.opacityColor};
    border-radius: 8px;
    margin-bottom: 8px;
    overflow: hidden;

    &:last-child {
        margin-bottom: 0;
    }
`;

const Question = styled.button`
    width: 100%;
    padding: 14px 16px;
    text-align: left;
    background: transparent;
    border: none;
    color: ${({ theme }) => theme.colors.cardText};
    font-size: 14px;
    font-weight: 500;
    font-family: ${({ theme }) => theme.colors.ff};
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    transition: all 0.2s ease;

    &:hover {
        background: ${({ theme }) => theme.colors.activeColor}08;
    }
`;

const QuestionText = styled.span`
    flex: 1;
`;

const Arrow = styled.span`
    color: ${({ theme }) => theme.colors.subTextColor};
    font-size: 16px;
    transition: transform 0.2s ease;
    transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    display: flex;
    align-items: center;
`;

const Answer = styled.div`
    padding: ${({ $isOpen }) => $isOpen ? '0 16px 16px 16px' : '0 16px'};
    max-height: ${({ $isOpen }) => $isOpen ? '600px' : '0'};
    opacity: ${({ $isOpen }) => $isOpen ? '1' : '0'};
    overflow: hidden;
    transition: all 0.25s ease;
    font-size: 14px;
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.subTextColor};
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    margin: 28px 0;
    
    &::before, &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: ${({ theme }) => theme.colors.borderColor};
    }
`;

const DividerText = styled.span`
    padding: 0 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.subTextColor};
`;

const FAQPage = () => {
    const [openItems, setOpenItems] = useState({});

    const toggleItem = (id) => {
        setOpenItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const faqData = {
        "Data & Results": [
            {
                id: "no-results",
                question: "I ran a query but got no results",
                answer: "No listings found for that property type in that ZIP code. Our listing data provider may have single-family listings but no multi-family, or vice versa. This is simply a reflection of what's currently on the market. Try a different ZIP or switch property types."
            },
            {
                id: "missing-zips",
                question: "I'm not seeing properties from some of my ZIP codes",
                answer: "Some ZIPs have no active listings for the selected property type — this is simply a reflection of what's currently on the market. Additionally, properties without square footage are filtered out since we can't calculate rent per sqft without it. This is a result of either no properties listed or the listing agent not including full property details in the listing."
            },
            {
                id: "no-rent-data",
                question: "Rent/Sqft shows 'No data found'",
                answer: "Our data provider doesn't have rental data for that ZIP. This is common in rural areas or ZIPs with very few rental listings. For multi-family, we try Multi-Family → Apartment → Single Family data as fallbacks to try as much as possible to get you a close result."
            },
            {
                id: "unknown-units",
                question: "Why do some MF properties show 'Unknown' unit count?",
                answer: "Due to the way MLS was initially created, it was never meant for multi-family properties, yet it is still used for them. This means that the only way to get unit count is through the description, which is not always accurate. The MLS description didn't contain enough detail for our AI to determine the unit count. This is merely a product of how the listing agent wrote the description. We do have around a 90% success rate with our AI parsing, but it's not perfect. We automatically retry parsing after 7 days in case the listing description is updated."
            },
            {
                id: "skipped-properties",
                question: "Why are some properties skipped in the results?",
                answer: "Properties are skipped if they're missing square footage, missing price, or (for multi-family) have an unknown unit count. These properties can't be accurately analyzed. This is typically a result of the listing agent not including full property details in the MLS listing — unfortunately, there's nothing we can do about incomplete source data."
            }
        ],
        "Calculations": [
            {
                id: "assumptions",
                question: "What assumptions do the calculations make?",
                answer: "All calculations assume you are paying the mortgage as outlined in standard industry terms — no overpayments, no lump sum payoffs, just regular scheduled payments over the amortization period. Rent and expenses grow at the rates you specify, compounding annually."
            },
            {
                id: "capex-calc",
                question: "How is CapEx calculated?",
                answer: "CapEx reserves are calculated using a base-plus-variable cost model: (Base Cost + Sqft × Rate) × Tier Multiplier. For example, HVAC has a base cost of $4,500 plus $3.50/sqft. A 1,200 sqft property at Standard tier: ($4,500 + 1,200 × $3.50) × 1.0 = $8,700 replacement cost. The annual reserve is then: Replacement Cost ÷ (Lifespan − Current Age). If that HVAC is 10 years old with a 20-year lifespan: $8,700 ÷ 10 = $870/year reserved."
            },
            {
                id: "capex-age-cost",
                question: "Why do I enter both age AND replacement cost for CapEx items?",
                answer: "The age tells us how much useful life remains. The replacement cost tells us what you'll eventually pay. Together, they calculate how much to reserve annually. A brand new roof (age 0) with a 25-year lifespan and $8,000 replacement cost reserves $320/year. The same roof at 20 years old reserves $1,600/year because replacement is imminent."
            },
            {
                id: "capex-zero",
                question: "What if I set a CapEx replacement cost to $0?",
                answer: "That item is excluded from the CapEx reserve calculation entirely. The item still exists — you're just telling the calculator you don't want to factor it into your annual reserves."
            },
            {
                id: "irr-decrease",
                question: "Why does IRR decrease over longer hold periods?",
                answer: "IRR is an annualized return. Getting your money back plus profit in 1 year equals a very high annualized return. The same total profit spread over 7 years equals a lower annualized return. A 23% IRR over 7 years is still excellent — it means your money compounds at 23% annually."
            },
            {
                id: "coc-vs-irr",
                question: "Why is my Cash-on-Cash different from IRR?",
                answer: "Cash-on-Cash (CoC) measures annual cash flow versus your initial investment. IRR factors in appreciation, loan paydown, and sale proceeds over the entire hold period. CoC is a year-by-year snapshot; IRR is total investment performance."
            },
            {
                id: "zip-capex",
                question: "What CapEx assumptions are used in ZIP mode?",
                answer: "ZIP mode calculates CapEx per-property using each property's actual square footage. Base costs and per-sqft rates are based on 2026 market research. The tier you select (Budget, Standard, Premium) applies multipliers reflecting labor costs. The condition you select (Outdated, Current, New) estimates component ages as a percentage of their lifespan."
            },
            {
                id: "few-pass",
                question: "Why do so few properties pass screening now?",
                answer: "Our CapEx calculations use realistic 2026 replacement costs scaled to each property's square footage. This is more accurate than flat estimates, but it means properties with high CapEx reserves relative to their rent may not meet your COC% threshold. This is intentional — it prevents buying properties that look profitable on paper but drain cash when systems need replacement. The properties that DO pass are genuinely good deals."
            },
            {
                id: "find-passing",
                question: "How do I find properties that pass screening?",
                answer: "Several factors affect whether properties pass: Tier (Budget uses lower cost multipliers than Standard), True DIY toggle (for licensed/all-DIY users), Condition (New assumes 10% of lifespan used vs Outdated at 90%), COC% threshold (lowering passes more properties), and excluding items by setting their base cost to $0. If very few properties pass with Standard tier and Outdated condition, that's valuable information — the market is tight."
            },
            {
                id: "budget-vs-diy",
                question: "What's the difference between Budget and True DIY?",
                answer: "Budget assumes you're using budget contractors or doing simple DIY work (40-95% of Standard costs). True DIY is for users who are licensed contractors or genuinely doing all work themselves — including HVAC, plumbing, and electrical. It applies deeper discounts (35-60% of Standard) on labor-heavy items. Only select this if you can legally and competently perform licensed trade work."
            },
            {
                id: "mf-capex",
                question: "How does CapEx differ for multi-family properties?",
                answer: "Multi-family properties have additional CapEx scaling: per-unit items (HVAC, water heaters, appliances, cabinets) use scaled multipliers based on unit count, while building-level items (roof, structure, driveway) don't multiply. The multipliers aren't straight 2x/3x/4x — they account for bulk purchasing and labor efficiency when replacing multiple units at once."
            },
            {
                id: "sf-vs-mf",
                question: "Why does the same property show different results in SF vs MF mode?",
                answer: "If you analyze a property as single-family, it calculates CapEx for one set of systems. If you analyze it as multi-family, per-unit items (HVAC, water heaters, appliances, cabinets) are scaled by unit count. A duplex will have higher CapEx reserves than a single-family home of the same size because it has two of everything that's unit-specific."
            },
            {
                id: "mf-same-rent",
                question: "Why does my MF property show the same rent for all units?",
                answer: "Due to MLS data limitations, we don't have per-unit square footage for most multi-family properties. We calculate per-unit rent as: (rent_per_sqft × building_sqft) / unit_count, which assumes equal-sized units. This provides a reasonable estimate for screening purposes."
            },
            {
                id: "cash-purchase",
                question: "Can I model a cash purchase?",
                answer: "Yes, set LTV (Loan-to-Value) to 0%. The calculator will show no debt service, and your initial investment equals the full purchase price. All other calculations (CapEx, operating expenses, NOI) work the same way."
            },
            {
                id: "property-taxes",
                question: "How are property taxes calculated?",
                answer: "Property taxes are calculated as: Purchase Price × Tax Rate. We don't use a separate assessed value — the purchase price serves as the basis. Taxes increase annually at your specified property tax growth rate."
            },
            {
                id: "interest-only",
                question: "What if I have an interest-only loan?",
                answer: "Set the Interest-Only Period to the number of years you'll pay interest only. During that period, no principal is paid down and your monthly payment is lower. After the interest-only period ends, the loan amortizes normally over the remaining term."
            }
        ],
        "Account & Usage": [
            {
                id: "data-refresh",
                question: "How often is listing data refreshed?",
                answer: "Listings are refreshed every 24 hours per ZIP. Rent data and market metrics are refreshed every 7 days per ZIP."
            },
            {
                id: "query-count",
                question: "What counts as a query?",
                answer: "Each analysis you run — whether in ZIP mode or Manual mode — counts as 1 query against your plan's limit."
            }
        ],
        "Performance": [
            {
                id: "slow-first",
                question: "Why did my analysis take so long the first time?",
                answer: "The first query for a ZIP fetches listings, rent data, and market metrics from our data providers. This initial fetch can take 30-60 seconds depending on how many listings are in that ZIP. Subsequent queries use cached data and typically complete in 3-5 seconds."
            },
            {
                id: "mf-slower",
                question: "Why do MF analyses take longer than SF?",
                answer: "Due to MLS data limitations for multi-family properties, we use AI to parse each listing's description and extract unit details (beds, baths, unit count). This adds approximately 5-10 seconds for the first query of a ZIP. The parsed data is cached, so subsequent queries are much faster."
            },
            {
                id: "max-units",
                question: "What's the maximum number of units supported?",
                answer: "Currently 4 units (duplex through fourplex). Properties with 5+ units will only calculate rent for the first 4 units."
            }
        ],
        "Troubleshooting": [
            {
                id: "loading-stuck",
                question: "The page is loading but nothing happens",
                answer: "Check your internet connection. If the issue persists, try refreshing the page. Long initial loads (60+ seconds) are normal for the first query of the day as our servers capture the most up to date data."
            },
            {
                id: "calc-error",
                question: "I'm getting an error when I try to calculate",
                answer: "Ensure all required fields are filled in. Common issues: Purchase price must be greater than 0, at least one unit rent must be greater than 0, cap rate must be between 0 and 100, and LTV cannot exceed 100%."
            },
            {
                id: "subscription",
                question: "My subscription isn't showing the right plan",
                answer: "Subscription changes may take a few minutes to sync. Try logging out and back in. If the issue persists, contact support."
            }
        ]
    };

    const sectionKeys = Object.keys(faqData);

    return (
        <PageContainer>
            <FAQCard>
                <HeaderSection>
                    <IconWrapper>
                        <IoHelpCircleOutline />
                    </IconWrapper>
                    <Title>FAQ & Support</Title>
                    <Subtitle>Frequently asked questions about using PropNinja for real estate investment analysis.</Subtitle>
                </HeaderSection>
                
                {sectionKeys.map((sectionTitle, index) => (
                    <div key={sectionTitle}>
                        {index > 0 && (
                            <Divider>
                                <DividerText>{sectionTitle}</DividerText>
                            </Divider>
                        )}
                        <Section>
                            {index === 0 && <SectionTitle>{sectionTitle}</SectionTitle>}
                            {faqData[sectionTitle].map((item) => (
                                <FAQItem key={item.id}>
                                    <Question onClick={() => toggleItem(item.id)}>
                                        <QuestionText>{item.question}</QuestionText>
                                        <Arrow $isOpen={openItems[item.id]}>
                                            <IoChevronDown />
                                        </Arrow>
                                    </Question>
                                    <Answer $isOpen={openItems[item.id]}>
                                        {item.answer}
                                    </Answer>
                                </FAQItem>
                            ))}
                        </Section>
                    </div>
                ))}
            </FAQCard>
        </PageContainer>
    );
};

export default FAQPage;
